import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../../helpers/lpApi';

// Admin management of the LP business-sector catalog (self-growing: company
// free-text domains auto-add here). Backed by besthand-api adminListLpSectors +
// adminUpsertLpSector via the /lp-graphql proxy.
const Q_SECTORS = `query {
  adminListLpSectors { status errorMessage lpSectors { id name slug isConstruction autoAdded active groupLabel sortOrder } }
}`;
const M_UPSERT = `mutation($id:Int,$name:String,$isConstruction:Boolean,$groupLabel:String,$active:Boolean,$sortOrder:Int,$clearAutoAdded:Boolean){
  adminUpsertLpSector(id:$id,name:$name,isConstruction:$isConstruction,groupLabel:$groupLabel,active:$active,sortOrder:$sortOrder,clearAutoAdded:$clearAutoAdded){ status errorMessage lpSector { id } }
}`;

const RED = '#D32F2F';
const INK = '#1F2733';
const TEAL = '#0E9488';

function Sectors() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [q, setQ] = useState('');
  const [renaming, setRenaming] = useState({}); // id -> new name
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [newConstruction, setNewConstruction] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lpApi(Q_SECTORS);
      const env = res && res.data && res.data.adminListLpSectors;
      setSectors((env && env.lpSectors) || []);
    } catch (e) { setMsg({ ok: false, text: 'Could not load sectors.' }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsert = async (vars, id = 'new') => {
    setBusy(String(id)); setMsg(null);
    try {
      const res = await lpApi(M_UPSERT, vars);
      const env = res && res.data && res.data.adminUpsertLpSector;
      if (env && env.status === 200) { await load(); setMsg({ ok: true, text: 'Saved.' }); }
      else setMsg({ ok: false, text: (env && env.errorMessage) || 'Save failed.' });
    } catch (e) { setMsg({ ok: false, text: 'Save failed.' }); }
    setBusy('');
  };

  const addNew = async () => {
    if (newName.trim().length < 2) { setMsg({ ok: false, text: 'Sector name required.' }); return; }
    await upsert({ name: newName.trim(), isConstruction: newConstruction, groupLabel: newGroup || null, active: true });
    setNewName(''); setNewGroup(''); setNewConstruction(false);
  };

  const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '7px 12px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 6 });
  const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
  const td = { padding: 10, borderTop: '1px solid #f0f0f0', fontSize: 14 };
  const chip = (bg) => ({ padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: '#fff', background: bg });

  const filtered = sectors.filter((s) => !q || (s.name || '').toLowerCase().includes(q.toLowerCase()) || (s.groupLabel || '').toLowerCase().includes(q.toLowerCase()));
  const groups = {};
  filtered.forEach((s) => { const g = s.groupLabel || 'Ungrouped'; (groups[g] = groups[g] || []).push(s); });
  const groupNames = Object.keys(groups).sort();

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <h1 className="siteAdminPrimaryHeading">Large Projects — Sectors</h1>
      <p style={{ color: '#777', marginTop: -8, marginBottom: 16 }}>
        The company domain catalog. It grows automatically: when a company types a domain that
        isn&apos;t listed (&quot;Other — type your own&quot;), it appears here flagged <b>auto-added</b>.
        Toggle <b>Construction</b> to switch a sector between the full LP flow (licenses/photos/lien
        waivers) and the simplified flow.
      </p>

      {/* Add new */}
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>Add a sector</div>
        <input style={{ ...input, width: 260 }} placeholder="Sector name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <input style={{ ...input, width: 220 }} placeholder="Group (optional)" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} />
        <label style={{ marginRight: 12, fontWeight: 600 }}>
          <input type="checkbox" checked={newConstruction} onChange={(e) => setNewConstruction(e.target.checked)} /> Construction
        </label>
        <button style={btn(RED)} disabled={busy === 'new'} onClick={addNew}>Add sector</button>
        {msg ? <span style={{ marginLeft: 12, color: msg.ok ? TEAL : RED, fontWeight: 600 }}>{msg.text}</span> : null}
      </div>

      <input style={{ ...input, width: 320, marginBottom: 14 }} placeholder="Search sectors…" value={q} onChange={(e) => setQ(e.target.value)} />
      <span style={{ color: '#888', fontSize: 13 }}>{filtered.length} / {sectors.length} sectors</span>

      {loading && <div style={{ padding: 20 }}>Loading…</div>}
      {!loading && groupNames.map((g) => (
        <div key={g} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', fontWeight: 700, borderBottom: '1px solid #f0f0f0', color: INK }}>{g} <span style={{ color: '#aaa', fontWeight: 400 }}>({groups[g].length})</span></div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {groups[g].map((s) => (
                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.5 }}>
                  <td style={{ ...td, width: '40%' }}>
                    {renaming[s.id] !== undefined ? (
                      <span>
                        <input style={{ ...input, width: 220 }} value={renaming[s.id]} onChange={(e) => setRenaming({ ...renaming, [s.id]: e.target.value })} />
                        <button style={btn(TEAL)} disabled={busy === String(s.id)} onClick={async () => { await upsert({ id: s.id, name: renaming[s.id] }, s.id); const r = { ...renaming }; delete r[s.id]; setRenaming(r); }}>Save</button>
                        <button style={btn('#9E9E9E')} onClick={() => { const r = { ...renaming }; delete r[s.id]; setRenaming(r); }}>Cancel</button>
                      </span>
                    ) : (
                      <span>
                        <b>{s.name}</b> <span style={{ color: '#bbb', fontSize: 12 }}>{s.slug}</span>
                        {s.autoAdded ? <span style={{ ...chip('#F9A825'), marginLeft: 8 }}>auto-added</span> : null}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    {s.isConstruction
                      ? <span style={chip('#1565C0')}>Construction</span>
                      : <span style={chip('#6B7280')}>Simplified</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {renaming[s.id] === undefined && <button style={btn('#455A64')} onClick={() => setRenaming({ ...renaming, [s.id]: s.name })}>Rename</button>}
                    <button style={btn(s.isConstruction ? '#6B7280' : '#1565C0')} disabled={busy === String(s.id)} onClick={() => upsert({ id: s.id, isConstruction: !s.isConstruction }, s.id)}>
                      {s.isConstruction ? '→ Simplified' : '→ Construction'}
                    </button>
                    {s.autoAdded && <button style={btn(TEAL)} disabled={busy === String(s.id)} onClick={() => upsert({ id: s.id, clearAutoAdded: true }, s.id)}>Curate</button>}
                    <button style={btn(s.active ? '#C62828' : '#2E7D32')} disabled={busy === String(s.id)} onClick={() => upsert({ id: s.id, active: !s.active }, s.id)}>
                      {s.active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default Sectors;
